/**
 * Test/demo/dev data cleanup tool — internal admin only.
 *
 * Mounted at /admin/test-data-cleanup behind <InternalAdminRoute>. Additionally
 * gated by VITE_ENABLE_TEST_DATA_CLEANUP so it can be disabled per environment.
 *
 * Flow:
 *   1. Schema audit  — list tables + foreign keys discovered via PostgREST.
 *   2. Find candidates — surface test/demo/dev roots across the schema.
 *   3. Dry run        — server returns a per-table plan + a sha256 token over
 *                       the selection. Token is what cleanup will require.
 *   4. Cleanup        — typed confirmation 'DELETE TEST DATA' + dry-run token.
 *
 * No nav entry. URL access only.
 */

import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, AlertTriangle, Download, Copy, Database, Search, FlaskConical, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const ENABLED = import.meta.env.VITE_ENABLE_TEST_DATA_CLEANUP === "true";
const FN = "admin-cleanup-test-data";

interface SchemaTable { name: string; columns: string[]; fks: { column: string; refTable: string; refColumn: string }[]; kind?: "table" | "view"; }
interface Classification {
  totalTables: number;
  totalViews?: number;
  views?: string[];
  hasShopId: string[]; hasOrganizationId: string[]; hasRegionId: string[]; hasDistrictId: string[];
  hasTicketId: string[]; hasCustomerId: string[]; hasVehicleId: string[]; hasTechnicianId: string[];
  hasBayId: string[]; hasParkingSpotId: string[]; hasUserId: string[];
  hasSource: string[]; hasCreatedBy: string[];
  protected: string[]; needsReview: string[];
}
interface SchemaAudit { schema: SchemaTable[]; classification: Classification; }

interface IdName { id: string; name: string | null; }
interface TicketCandidate { id: string; ticket_number?: string | null; shop_id?: string; source?: string | null; created_by?: string | null; }
interface CustomerCandidate { id: string; full_name?: string | null; shop_id?: string; }
interface VehicleCandidate { id: string; shop_id?: string; make?: string | null; model?: string | null; }
interface OrphanCandidate { table: string; matchedBySource: number; matchedByCreatedBy: number; }
interface Candidates {
  shops: IdName[]; protectedShops: IdName[];
  organizations: IdName[]; regions: IdName[]; districts: IdName[];
  tickets: TicketCandidate[]; customers: CustomerCandidate[]; vehicles: VehicleCandidate[];
  orphanCandidates: OrphanCandidate[];
}

interface DryRunEntry {
  table: string;
  conditions: { col: string; ids: string[] }[];
  orphanFilters?: { col: string; op: "in" | "ilike"; val: unknown }[];
  estimatedCount: number;
  skipReason?: string;
}
interface DryRunResp {
  dryRunToken: string;
  selection: Selection;
  order: string[];
  entries: DryRunEntry[];
  derivedCounts: Record<string, number>;
  orderInversions?: { parent: string; child: string }[];
  cycleMembers?: string[];
}

interface Selection {
  shopIds: string[]; orgIds: string[]; regionIds: string[]; districtIds: string[];
  ticketIds: string[]; customerIds: string[]; vehicleIds: string[];
  orphanTables: string[]; acknowledgeRealShops?: boolean;
}

async function readErrorBody(error: unknown): Promise<any | null> {
  try {
    const ctx = (error as { context?: Response })?.context;
    if (ctx && typeof ctx.json === "function") return await ctx.clone().json();
  } catch { /* fall through */ }
  return null;
}

function emptySelection(): Selection {
  return { shopIds: [], orgIds: [], regionIds: [], districtIds: [], ticketIds: [], customerIds: [], vehicleIds: [], orphanTables: [] };
}

function toggle(arr: string[], id: string): string[] {
  return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
}

export default function TestDataCleanup() {
  const { toast } = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [schemaAudit, setSchemaAudit] = useState<SchemaAudit | null>(null);
  const [candidates, setCandidates] = useState<Candidates | null>(null);
  const [selection, setSelection] = useState<Selection>(emptySelection());
  const [acknowledgeReal, setAcknowledgeReal] = useState(false);
  const [dryRun, setDryRun] = useState<DryRunResp | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [cleanupResult, setCleanupResult] = useState<any | null>(null);
  // Fixed-order selected-shops cleanup state (separate from the universal
  // dryRun/cleanup flow above so they can't collide).
  const [fixedDryRun, setFixedDryRun] = useState<any | null>(null);
  const [fixedConfirmText, setFixedConfirmText] = useState("");
  const [fixedCleanupResult, setFixedCleanupResult] = useState<any | null>(null);

  const totalSelected = useMemo(
    () => selection.shopIds.length + selection.orgIds.length + selection.regionIds.length +
      selection.districtIds.length + selection.ticketIds.length + selection.customerIds.length +
      selection.vehicleIds.length + selection.orphanTables.length,
    [selection],
  );

  async function call(action: string, body: Record<string, unknown> = {}): Promise<any | null> {
    const { data, error } = await supabase.functions.invoke(FN, { body: { action, ...body } });
    if (error) {
      const errBody = await readErrorBody(error);
      toast({
        title: errBody?.step ? `${action} failed at ${errBody.step}` : `${action} failed`,
        description: errBody?.message ?? error.message,
        variant: "destructive",
      });
      return null;
    }
    return data;
  }

  async function runSchemaAudit() {
    setBusy("schema");
    const data = await call("schemaAudit");
    setBusy(null);
    if (data) setSchemaAudit(data);
  }

  async function runFindCandidates() {
    setBusy("candidates");
    const data = await call("auditCandidates");
    setBusy(null);
    if (data?.candidates) setCandidates(data.candidates);
  }

  async function runDryRun() {
    setBusy("dryRun");
    setDryRun(null);
    setCleanupResult(null);
    const data = await call("dryRun", {
      selection: { ...selection, acknowledgeRealShops: acknowledgeReal },
    });
    setBusy(null);
    if (data?.dryRunToken) setDryRun(data);
  }

  async function runCleanup() {
    if (!dryRun) return;
    setBusy("cleanup");
    setCleanupResult(null);
    const data = await call("cleanup", {
      selection: { ...selection, acknowledgeRealShops: acknowledgeReal },
      dryRunToken: dryRun.dryRunToken,
      confirmation: confirmText,
    });
    setBusy(null);
    if (data) {
      setCleanupResult(data);
      // Reset confirmation so a stale "DELETE TEST DATA" can't be re-fired.
      setConfirmText("");
    }
  }

  function copyJson() {
    if (!dryRun) return;
    navigator.clipboard.writeText(JSON.stringify(dryRun, null, 2));
    toast({ title: "Dry-run JSON copied" });
  }

  async function runFixedOrderDryRun() {
    if (selection.shopIds.length === 0) {
      toast({ title: "Pick at least one shop first", variant: "destructive" });
      return;
    }
    setBusy("fixedDryRun");
    setFixedDryRun(null);
    setFixedCleanupResult(null);
    const data = await call("dryRunSelectedShopsFixedOrder", {
      selection: { ...selection, acknowledgeRealShops: acknowledgeReal },
    });
    setBusy(null);
    if (data?.dryRunToken) setFixedDryRun(data);
  }

  async function runFixedOrderCleanup() {
    if (!fixedDryRun) return;
    setBusy("fixedCleanup");
    setFixedCleanupResult(null);
    const data = await call("cleanupSelectedShopsFixedOrder", {
      selection: { ...selection, acknowledgeRealShops: acknowledgeReal },
      dryRunToken: fixedDryRun.dryRunToken,
      confirmation: fixedConfirmText,
    });
    setBusy(null);
    if (data) {
      setFixedCleanupResult(data);
      setFixedConfirmText("");
    }
  }

  function downloadJson() {
    if (!dryRun) return;
    const blob = new Blob([JSON.stringify(dryRun, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `test-data-cleanup-dryrun-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!ENABLED) {
    return (
      <div className="p-8 max-w-2xl">
        <h1 className="text-2xl font-bold mb-2">Test Data Cleanup disabled</h1>
        <p className="text-muted-foreground">
          Set <code>VITE_ENABLE_TEST_DATA_CLEANUP=true</code> in your <code>.env.local</code> and reload.
        </p>
      </div>
    );
  }

  const dryRunInversions = dryRun?.orderInversions ?? [];
  const dryRunHasViews = dryRun ? dryRun.order.some((t) => /^v_|_views?$/i.test(t)) : false;
  const cleanupReady =
    !!dryRun &&
    confirmText === "DELETE TEST DATA" &&
    dryRunInversions.length === 0 &&
    !dryRunHasViews;
  const hasProtectedShopSelected = candidates?.protectedShops?.some((s) => selection.shopIds.includes(s.id)) ?? false;

  return (
    <div className="p-8 max-w-5xl space-y-6">
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
          <div>
            <h1 className="text-xl font-bold">Test / Demo / Dev Data Cleanup</h1>
            <p className="text-sm text-muted-foreground mt-1">
              This tool runs against the <strong>active Supabase project</strong> the app is connected to.
              It will not delete anything until you (1) pick roots, (2) run a dry-run, (3) type
              <code className="mx-1">DELETE TEST DATA</code>, and (4) click Clean.
              <code className="mx-1">OptiBayHQ</code> is filtered out of candidate audits by default.
            </p>
          </div>
        </div>
      </div>

      {/* STEP 1 — schema */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Database className="h-5 w-5" /> 1. Schema audit</CardTitle>
          <CardDescription>List public tables, key columns, and FK edges discovered through PostgREST. Read-only.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={runSchemaAudit} disabled={busy !== null}>
            {busy === "schema" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading…</> : "Run schema audit"}
          </Button>
          {schemaAudit && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              <div><strong>Total relations:</strong> {schemaAudit.classification.totalTables}</div>
              <div><strong>Views (read-only):</strong> {schemaAudit.classification.totalViews ?? 0}</div>
              <div><strong>shop_id:</strong> {schemaAudit.classification.hasShopId.length}</div>
              <div><strong>organization_id:</strong> {schemaAudit.classification.hasOrganizationId.length}</div>
              <div><strong>ticket_id:</strong> {schemaAudit.classification.hasTicketId.length}</div>
              <div><strong>customer_id:</strong> {schemaAudit.classification.hasCustomerId.length}</div>
              <div><strong>vehicle_id:</strong> {schemaAudit.classification.hasVehicleId.length}</div>
              <div><strong>technician_id:</strong> {schemaAudit.classification.hasTechnicianId.length}</div>
              <div><strong>bay_id:</strong> {schemaAudit.classification.hasBayId.length}</div>
              <div><strong>parking_spot_id:</strong> {schemaAudit.classification.hasParkingSpotId.length}</div>
              <div><strong>source:</strong> {schemaAudit.classification.hasSource.length}</div>
              <div><strong>created_by:</strong> {schemaAudit.classification.hasCreatedBy.length}</div>
              <div><strong>protected:</strong> {schemaAudit.classification.protected.length}</div>
              <div><strong>needs review:</strong> {schemaAudit.classification.needsReview.length}</div>
            </div>
          )}
          {schemaAudit && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">Show protected / needs-review tables</summary>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <p className="font-semibold mb-1">Protected (will never touch)</p>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {schemaAudit.classification.protected.map((t) => <li key={t}>{t}</li>)}
                  </ul>
                </div>
                <div>
                  <p className="font-semibold mb-1">Needs manual review</p>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {schemaAudit.classification.needsReview.map((t) => <li key={t}>{t}</li>)}
                  </ul>
                </div>
              </div>
            </details>
          )}
        </CardContent>
      </Card>

      {/* STEP 2 — candidates */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Search className="h-5 w-5" /> 2. Candidate test / demo / dev roots</CardTitle>
          <CardDescription>
            Surfaces obvious test-looking shops/orgs/regions/districts plus marker-stamped tickets/customers/vehicles. The exact name "OptiBayHQ" is filtered out by default.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={runFindCandidates} disabled={busy !== null}>
            {busy === "candidates" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Searching…</> : "Find candidates"}
          </Button>

          {candidates && (
            <div className="space-y-4">
              <CandidateGroup
                title="Shops"
                helper={candidates.protectedShops.length > 0 ? `${candidates.protectedShops.length} protected shop(s) filtered: ${candidates.protectedShops.map((s) => s.name).join(", ")}` : undefined}
                items={candidates.shops}
                selectedIds={selection.shopIds}
                onToggle={(id) => setSelection((s) => ({ ...s, shopIds: toggle(s.shopIds, id) }))}
                renderLabel={(s) => `${s.name ?? "(unnamed)"} — ${s.id.slice(0, 8)}`}
              />
              <CandidateGroup
                title="Organizations" items={candidates.organizations} selectedIds={selection.orgIds}
                onToggle={(id) => setSelection((s) => ({ ...s, orgIds: toggle(s.orgIds, id) }))}
                renderLabel={(o) => `${o.name ?? "(unnamed)"} — ${o.id.slice(0, 8)}`}
              />
              <CandidateGroup
                title="Regions" items={candidates.regions} selectedIds={selection.regionIds}
                onToggle={(id) => setSelection((s) => ({ ...s, regionIds: toggle(s.regionIds, id) }))}
                renderLabel={(r) => `${r.name ?? "(unnamed)"} — ${r.id.slice(0, 8)}`}
              />
              <CandidateGroup
                title="Districts" items={candidates.districts} selectedIds={selection.districtIds}
                onToggle={(id) => setSelection((s) => ({ ...s, districtIds: toggle(s.districtIds, id) }))}
                renderLabel={(d) => `${d.name ?? "(unnamed)"} — ${d.id.slice(0, 8)}`}
              />
              <CandidateGroup
                title="Tickets (marker-tagged)" items={candidates.tickets} selectedIds={selection.ticketIds}
                onToggle={(id) => setSelection((s) => ({ ...s, ticketIds: toggle(s.ticketIds, id) }))}
                renderLabel={(t) => `${t.ticket_number ?? t.id.slice(0, 8)} · source=${t.source ?? ""} · created_by=${t.created_by ?? ""}`}
              />
              <CandidateGroup
                title="Customers (DEMO •)" items={candidates.customers} selectedIds={selection.customerIds}
                onToggle={(id) => setSelection((s) => ({ ...s, customerIds: toggle(s.customerIds, id) }))}
                renderLabel={(c) => `${c.full_name ?? "(unnamed)"} — ${c.id.slice(0, 8)}`}
              />
              <CandidateGroup
                title="Vehicles (test-named)" items={candidates.vehicles} selectedIds={selection.vehicleIds}
                onToggle={(id) => setSelection((s) => ({ ...s, vehicleIds: toggle(s.vehicleIds, id) }))}
                renderLabel={(v) => `${v.make ?? ""} ${v.model ?? ""} — ${v.id.slice(0, 8)}`}
              />

              <div>
                <p className="text-sm font-semibold mb-2">Orphan sweep candidates ({candidates.orphanCandidates.length} tables)</p>
                <p className="text-xs text-muted-foreground mb-2">
                  Tables with source / created_by markers but no parent shop selected. Only swept if you opt in here.
                </p>
                <ScrollArea className="h-48 border rounded-md p-2">
                  <div className="space-y-1">
                    {candidates.orphanCandidates.map((o) => (
                      <label key={o.table} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={selection.orphanTables.includes(o.table)}
                          onCheckedChange={() => setSelection((s) => ({ ...s, orphanTables: toggle(s.orphanTables, o.table) }))}
                        />
                        <span className="font-mono text-xs">{o.table}</span>
                        <span className="text-xs text-muted-foreground">
                          source={o.matchedBySource} · created_by={o.matchedByCreatedBy}
                        </span>
                      </label>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* STEP 3 — dry run */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FlaskConical className="h-5 w-5" /> 3. Dry run</CardTitle>
          <CardDescription>
            Computes per-table delete counts in dependency order. Returns a dryRunToken; cleanup will reject any selection that doesn't match.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm">
            Selected: <strong>{totalSelected}</strong> roots —{" "}
            shops {selection.shopIds.length} · orgs {selection.orgIds.length} ·
            regions {selection.regionIds.length} · districts {selection.districtIds.length} ·
            tickets {selection.ticketIds.length} · customers {selection.customerIds.length} ·
            vehicles {selection.vehicleIds.length} · orphan tables {selection.orphanTables.length}
          </div>
          {hasProtectedShopSelected && (
            <label className="flex items-center gap-2 text-sm text-destructive">
              <Checkbox checked={acknowledgeReal} onCheckedChange={(v) => setAcknowledgeReal(!!v)} />
              I acknowledge a protected real shop is in the selection (acknowledgeRealShops)
            </label>
          )}
          <div className="flex gap-2">
            <Button onClick={runDryRun} disabled={busy !== null || totalSelected === 0}>
              {busy === "dryRun" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Planning…</> : "Run dry run"}
            </Button>
            {dryRun && (
              <>
                <Button variant="outline" onClick={copyJson}><Copy className="mr-2 h-4 w-4" />Copy JSON</Button>
                <Button variant="outline" onClick={downloadJson}><Download className="mr-2 h-4 w-4" />Download JSON</Button>
              </>
            )}
          </div>

          {dryRun && (() => {
            const executable = dryRun.order
              .map((name, i) => ({ entry: dryRun.entries.find((e) => e.table === name), idx: i + 1 }))
              .filter((x): x is { entry: DryRunEntry; idx: number } => !!x.entry);
            const skipped = dryRun.entries.filter((e) => e.skipReason);
            const viewSkips = skipped.filter((e) => (e.skipReason ?? "").startsWith("view/"));
            const protectedSkips = skipped.filter((e) => e.skipReason === "protected");
            const reviewSkips = skipped.filter((e) => e.skipReason === "needs manual review");
            const viewsInOrder = dryRun.order.filter((t) => /^v_|_views?$/i.test(t));
            return (
              <div className="space-y-2 text-sm">
                <div className="text-xs text-muted-foreground">
                  <strong>dryRunToken:</strong> <span className="font-mono">{dryRun.dryRunToken}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  <strong>Derived IDs:</strong> tickets {dryRun.derivedCounts.ticketIds} · customers {dryRun.derivedCounts.customerIds} ·
                  vehicles {dryRun.derivedCounts.vehicleIds} · technicians {dryRun.derivedCounts.technicianIds} ·
                  bays {dryRun.derivedCounts.bayIds} · ticket_jobs {dryRun.derivedCounts.ticketJobIds}
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-background">
                    <strong>{executable.length}</strong> executable
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-muted/40 text-muted-foreground">
                    {viewSkips.length} views (read-only)
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-muted/40 text-muted-foreground">
                    {protectedSkips.length} protected
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-muted/40 text-muted-foreground">
                    {reviewSkips.length} needs review
                  </span>
                </div>
                {viewsInOrder.length > 0 && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
                    ⚠ View(s) appeared in the executable order list: {viewsInOrder.join(", ")} — refusing to run cleanup.
                  </div>
                )}
                {(dryRun.orderInversions?.length ?? 0) > 0 && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
                    ⚠ Order inversion(s) — cleanup will refuse to run:
                    <ul className="list-disc pl-5 mt-1">
                      {dryRun.orderInversions!.slice(0, 30).map((i, idx) => (
                        <li key={idx}>
                          <span className="font-mono">{i.parent}</span> ordered before child{" "}
                          <span className="font-mono">{i.child}</span>
                        </li>
                      ))}
                      {(dryRun.orderInversions!.length > 30) && (
                        <li className="text-muted-foreground">… and {dryRun.orderInversions!.length - 30} more</li>
                      )}
                    </ul>
                  </div>
                )}
                {(dryRun.cycleMembers?.length ?? 0) > 0 && (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300">
                    ⚠ FK cycle(s) detected among in-scope tables — placed in best-effort child-first order ({dryRun.cycleMembers!.length} table(s)):
                    <div className="font-mono mt-1 break-words">{dryRun.cycleMembers!.join(", ")}</div>
                  </div>
                )}

                <p className="text-xs font-semibold mt-2">Executable delete order ({executable.length})</p>
                <ScrollArea className="h-64 border rounded-md p-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        <th className="py-1 pr-2 w-12">#</th>
                        <th className="py-1 pr-2">Table</th>
                        <th className="py-1 pr-2">Match cols</th>
                        <th className="py-1 pr-2 text-right">Estimated rows</th>
                      </tr>
                    </thead>
                    <tbody>
                      {executable.map(({ entry: e, idx }) => {
                        const matchCols = [
                          ...e.conditions.map((c) => c.col),
                          ...(e.orphanFilters?.map((f) => `orphan:${f.col}`) ?? []),
                        ].join(", ");
                        return (
                          <tr key={e.table} className="border-t border-muted/40">
                            <td className="py-1 pr-2 text-muted-foreground">{idx}</td>
                            <td className="py-1 pr-2 font-mono">{e.table}</td>
                            <td className="py-1 pr-2 font-mono">{matchCols || "—"}</td>
                            <td className="py-1 pr-2 text-right">{e.estimatedCount < 0 ? "—" : e.estimatedCount}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </ScrollArea>

                <p className="text-xs font-semibold mt-3">Skipped — will NOT be deleted ({skipped.length})</p>
                <ScrollArea className="h-48 border rounded-md p-2 bg-muted/20">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        <th className="py-1 pr-2 w-32">Status</th>
                        <th className="py-1 pr-2">Table</th>
                        <th className="py-1 pl-2">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skipped.map((e) => {
                        const r = e.skipReason ?? "";
                        const cls = r.startsWith("view/")
                          ? "text-blue-600 dark:text-blue-400"
                          : r === "protected"
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-muted-foreground";
                        const label = r.startsWith("view/") ? "view (read-only)" : r;
                        return (
                          <tr key={e.table} className="border-t border-muted/40">
                            <td className={`py-1 pr-2 font-semibold ${cls}`}>{label}</td>
                            <td className="py-1 pr-2 font-mono">{e.table}</td>
                            <td className="py-1 pl-2 text-muted-foreground">{r}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* STEP 4 — cleanup */}
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive"><Trash2 className="h-5 w-5" /> 4. Cleanup</CardTitle>
          <CardDescription>
            Type <code>DELETE TEST DATA</code> to enable. The button stays disabled until you have a current dry-run.
            Cleanup re-resolves the plan server-side and stops on the first table-level error.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="confirm">Typed confirmation</Label>
            <Input
              id="confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE TEST DATA"
              autoComplete="off"
              className="font-mono"
              disabled={!dryRun}
            />
          </div>
          <Button
            variant="destructive"
            onClick={runCleanup}
            disabled={!cleanupReady || busy !== null}
          >
            {busy === "cleanup" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Cleaning…</> : "Run cleanup"}
          </Button>

          {cleanupResult && (
            <div className="space-y-3">
              <div className={cleanupResult.incomplete ? "rounded-md border border-destructive/40 bg-destructive/5 p-3" : "rounded-md border bg-muted/30 p-3"}>
                <p className="text-sm">
                  <strong>Total deleted:</strong> {cleanupResult.totalDeleted ?? 0}
                  {" · "}
                  <strong>Status:</strong>{" "}
                  {cleanupResult.incomplete
                    ? <span className="text-destructive">INCOMPLETE</span>
                    : <span>complete</span>}
                  {cleanupResult.stoppedAt ? <span className="text-destructive"> · stopped at {cleanupResult.stoppedAt}</span> : null}
                </p>
                {cleanupResult.incompleteReason && (
                  <p className="text-xs text-destructive mt-1">{cleanupResult.incompleteReason}</p>
                )}
              </div>

              {cleanupResult.preParentBlockers?.length > 0 && (
                <div className="rounded-md border border-destructive/40 p-3">
                  <p className="text-sm font-semibold text-destructive">
                    Pre-parent guard — refused to delete {cleanupResult.stoppedAt} because these refs remain
                  </p>
                  <table className="w-full text-xs mt-2">
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        <th className="py-1 pr-2">Ref table</th>
                        <th className="py-1 pr-2">Ref column</th>
                        <th className="py-1 pr-2 text-right">Rows</th>
                        <th className="py-1 pl-2">In plan?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cleanupResult.preParentBlockers.map((b: { table: string; column: string; count: number; inScope: boolean }) => (
                        <tr key={`${b.table}.${b.column}`} className="border-t border-muted/40">
                          <td className="py-1 pr-2 font-mono">{b.table}</td>
                          <td className="py-1 pr-2 font-mono">{b.column}</td>
                          <td className="py-1 pr-2 text-right">{b.count}</td>
                          <td className="py-1 pl-2">{b.inScope ? "yes" : <span className="text-destructive">no</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {cleanupResult.verification && (
                <RemainingRoots verification={cleanupResult.verification} />
              )}

              <details>
                <summary className="cursor-pointer text-sm">Per-table delete counts</summary>
                <ScrollArea className="h-64 border rounded-md p-2 mt-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        <th className="py-1 pr-2">Table</th>
                        <th className="py-1 pr-2 text-right">Deleted</th>
                        <th className="py-1 pl-2">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(cleanupResult.results ?? []).map((r: { table: string; deleted: number; error?: string }) => (
                        <tr key={r.table} className="border-t border-muted/40">
                          <td className="py-1 pr-2 font-mono">{r.table}</td>
                          <td className="py-1 pr-2 text-right">{r.deleted}</td>
                          <td className="py-1 pl-2 text-destructive">{r.error ?? ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </details>
            </div>
          )}
        </CardContent>
      </Card>

      {/* FIXED-ORDER MODE — independent path that uses a hand-curated delete
          order instead of dynamic topo sort. Use this for the immediate
          test-shop cleanup when the universal flow keeps blocking on
          inversions. */}
      <Card className="border-amber-500/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            Fixed-order cleanup (selected shops only)
          </CardTitle>
          <CardDescription>
            Deterministic path: deletes child rows in a hand-curated order,
            then shops last. Uses only your selected <strong>shopIds</strong>.
            Same protections — typed confirmation, dry-run token, OptiBayHQ guard,
            view skipping, post-cleanup verification.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm">
            Selected shops: <strong>{selection.shopIds.length}</strong>
          </div>
          <Button onClick={runFixedOrderDryRun} disabled={busy !== null || selection.shopIds.length === 0}>
            {busy === "fixedDryRun" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Planning…</> : "Fixed-order dry-run"}
          </Button>

          {fixedDryRun && (
            <div className="space-y-2 text-sm">
              <div className="text-xs text-muted-foreground">
                <strong>dryRunToken:</strong> <span className="font-mono">{fixedDryRun.dryRunToken}</span>
              </div>
              {fixedDryRun.tailSweepTables?.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <strong>Tail sweep adds {fixedDryRun.tailSweepTables.length} table(s):</strong>{" "}
                  <span className="font-mono break-words">{fixedDryRun.tailSweepTables.join(", ")}</span>
                </div>
              )}
              <ScrollArea className="h-72 border rounded-md p-2">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="py-1 pr-2 w-10">#</th>
                      <th className="py-1 pr-2">Table</th>
                      <th className="py-1 pr-2">by</th>
                      <th className="py-1 pr-2">ids src</th>
                      <th className="py-1 pr-2">mode</th>
                      <th className="py-1 pr-2 text-right">Rows</th>
                      <th className="py-1 pl-2">Skip / Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(fixedDryRun.steps ?? []).map((s: any, i: number) => {
                      const modeColor =
                        s.mode === "rpc" ? "text-blue-600 dark:text-blue-400"
                        : s.mode === "batched_id" ? "text-amber-700 dark:text-amber-300"
                        : "text-muted-foreground";
                      return (
                        <tr key={`${s.table}:${s.by}:${i}`} className="border-t border-muted/40">
                          <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
                          <td className="py-1 pr-2 font-mono">{s.table}</td>
                          <td className="py-1 pr-2 font-mono">{s.by}</td>
                          <td className="py-1 pr-2 font-mono text-muted-foreground">{s.idSource}</td>
                          <td className={`py-1 pr-2 font-mono ${modeColor}`}>{s.mode ?? "direct"}</td>
                          <td className="py-1 pr-2 text-right">{s.skipped ? "—" : s.count}</td>
                          <td className={`py-1 pl-2 ${s.error ? "text-destructive" : "text-muted-foreground"}`}>
                            {s.error ?? s.skipped ?? ""}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ScrollArea>

              <div>
                <Label htmlFor="fixed-confirm">Typed confirmation</Label>
                <Input
                  id="fixed-confirm"
                  value={fixedConfirmText}
                  onChange={(e) => setFixedConfirmText(e.target.value)}
                  placeholder="DELETE TEST DATA"
                  autoComplete="off"
                  className="font-mono"
                />
              </div>
              <Button
                variant="destructive"
                onClick={runFixedOrderCleanup}
                disabled={
                  busy !== null ||
                  fixedConfirmText !== "DELETE TEST DATA" ||
                  selection.shopIds.length === 0
                }
              >
                {busy === "fixedCleanup" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Cleaning…</> : "Run fixed-order cleanup"}
              </Button>
            </div>
          )}

          {fixedCleanupResult && (
            <div className="space-y-3">
              <div className={fixedCleanupResult.incomplete ? "rounded-md border border-destructive/40 bg-destructive/5 p-3" : "rounded-md border bg-muted/30 p-3"}>
                <p className="text-sm">
                  <strong>Total deleted:</strong> {fixedCleanupResult.totalDeleted ?? 0}
                  {" · "}
                  <strong>Status:</strong>{" "}
                  {fixedCleanupResult.incomplete
                    ? <span className="text-destructive">INCOMPLETE</span>
                    : <span>complete</span>}
                  {fixedCleanupResult.stoppedAt?.table ? <span className="text-destructive"> · stopped at {fixedCleanupResult.stoppedAt.table}</span> : null}
                </p>
                {fixedCleanupResult.incompleteReason && (
                  <p className="text-xs text-destructive mt-1">{fixedCleanupResult.incompleteReason}</p>
                )}
              </div>

              {fixedCleanupResult.verification && (
                <RemainingRoots verification={fixedCleanupResult.verification} />
              )}

              <details>
                <summary className="cursor-pointer text-sm">Per-step delete counts</summary>
                <ScrollArea className="h-64 border rounded-md p-2 mt-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        <th className="py-1 pr-2 w-10">#</th>
                        <th className="py-1 pr-2">Table</th>
                        <th className="py-1 pr-2">by</th>
                        <th className="py-1 pr-2">mode</th>
                        <th className="py-1 pr-2 text-right">Deleted</th>
                        <th className="py-1 pr-2 text-right">Attempted</th>
                        <th className="py-1 pl-2">Skip / Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(fixedCleanupResult.steps ?? []).map((s: any, i: number) => {
                        const modeColor =
                          s.mode === "rpc" ? "text-blue-600 dark:text-blue-400"
                          : s.mode === "batched_id" ? "text-amber-700 dark:text-amber-300"
                          : "text-muted-foreground";
                        const errDisplay = s.error
                          ? `${s.phase ? `[${s.phase}] ` : ""}${s.error}`
                          : s.skipped ?? "";
                        return (
                          <tr key={`${s.table}:${s.by}:${i}`} className="border-t border-muted/40">
                            <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
                            <td className="py-1 pr-2 font-mono">{s.table}</td>
                            <td className="py-1 pr-2 font-mono">{s.by}</td>
                            <td className={`py-1 pr-2 font-mono ${modeColor}`}>{s.mode ?? "direct"}</td>
                            <td className="py-1 pr-2 text-right">{s.skipped ? "—" : s.count}</td>
                            <td className="py-1 pr-2 text-right text-muted-foreground">{s.attempted ?? ""}</td>
                            <td className={`py-1 pl-2 ${s.error ? "text-destructive" : "text-muted-foreground"}`}>
                              {errDisplay}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </ScrollArea>
              </details>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface VerificationView {
  remainingSelectedShops: { id: string; name: string | null }[];
  remainingSelectedOrganizations: { id: string; name: string | null }[];
  remainingSelectedRegions: { id: string; name: string | null }[];
  remainingSelectedDistricts: { id: string; name: string | null }[];
  remainingSelectedTickets: { id: string; shop_id?: string | null }[];
  remainingSelectedCustomers: { id: string; shop_id?: string | null }[];
  remainingSelectedVehicles: { id: string; shop_id?: string | null }[];
  blockingReferences: { rootTable: string; refTable: string; refColumn: string; count: number; inScope: boolean }[];
}

function RemainingRoots({ verification }: { verification: VerificationView }) {
  const sections: { title: string; rows: { label: string; sub?: string }[] }[] = [
    { title: "Shops still present", rows: verification.remainingSelectedShops.map((r) => ({ label: r.name ?? "(unnamed)", sub: r.id })) },
    { title: "Organizations still present", rows: verification.remainingSelectedOrganizations.map((r) => ({ label: r.name ?? "(unnamed)", sub: r.id })) },
    { title: "Regions still present", rows: verification.remainingSelectedRegions.map((r) => ({ label: r.name ?? "(unnamed)", sub: r.id })) },
    { title: "Districts still present", rows: verification.remainingSelectedDistricts.map((r) => ({ label: r.name ?? "(unnamed)", sub: r.id })) },
    { title: "Tickets still present", rows: verification.remainingSelectedTickets.map((r) => ({ label: r.id })) },
    { title: "Customers still present", rows: verification.remainingSelectedCustomers.map((r) => ({ label: r.id })) },
    { title: "Vehicles still present", rows: verification.remainingSelectedVehicles.map((r) => ({ label: r.id })) },
  ].filter((s) => s.rows.length > 0);

  const blocks = verification.blockingReferences;
  if (sections.length === 0 && blocks.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Verification: all selected root rows were successfully deleted.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {sections.length > 0 && (
        <div className="rounded-md border border-destructive/40 p-3">
          <p className="text-sm font-semibold text-destructive">Selected rows still present after cleanup</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            {sections.map((s) => (
              <div key={s.title}>
                <p className="text-xs font-semibold">{s.title} ({s.rows.length})</p>
                <ul className="text-xs space-y-0.5 mt-1">
                  {s.rows.slice(0, 20).map((r) => (
                    <li key={`${s.title}:${r.label}:${r.sub ?? ""}`} className="font-mono">
                      {r.label}{r.sub ? <span className="text-muted-foreground"> — {r.sub}</span> : null}
                    </li>
                  ))}
                  {s.rows.length > 20 && <li className="text-muted-foreground">… and {s.rows.length - 20} more</li>}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
      {blocks.length > 0 && (
        <div className="rounded-md border p-3">
          <p className="text-sm font-semibold">Blocking foreign-key references</p>
          <p className="text-xs text-muted-foreground mt-1">
            These tables still hold rows pointing at the selected roots. Add them to your selection
            (or clean them manually) and re-run cleanup.
          </p>
          <table className="w-full text-xs mt-2">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-2">Root</th>
                <th className="py-1 pr-2">Ref table</th>
                <th className="py-1 pr-2">Ref column</th>
                <th className="py-1 pr-2 text-right">Rows</th>
                <th className="py-1 pl-2">In plan?</th>
              </tr>
            </thead>
            <tbody>
              {blocks.map((b, i) => (
                <tr key={`${b.refTable}.${b.refColumn}:${i}`} className="border-t border-muted/40">
                  <td className="py-1 pr-2 font-mono">{b.rootTable}</td>
                  <td className="py-1 pr-2 font-mono">{b.refTable}</td>
                  <td className="py-1 pr-2 font-mono">{b.refColumn}</td>
                  <td className="py-1 pr-2 text-right">{b.count}</td>
                  <td className="py-1 pl-2">{b.inScope ? "yes" : <span className="text-destructive">no</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CandidateGroup<T extends { id: string }>(props: {
  title: string;
  helper?: string;
  items: T[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  renderLabel: (item: T) => string;
}) {
  const { title, helper, items, selectedIds, onToggle, renderLabel } = props;
  if (items.length === 0) {
    return (
      <details className="text-sm">
        <summary className="cursor-pointer">{title} — 0 candidates</summary>
        {helper && <p className="text-xs text-muted-foreground mt-1">{helper}</p>}
      </details>
    );
  }
  return (
    <details className="text-sm" open={items.length <= 20}>
      <summary className="cursor-pointer">{title} — {items.length} candidates ({selectedIds.length} selected)</summary>
      {helper && <p className="text-xs text-muted-foreground mt-1">{helper}</p>}
      <ScrollArea className="h-48 border rounded-md p-2 mt-2">
        <div className="space-y-1">
          {items.map((it) => (
            <label key={it.id} className="flex items-center gap-2 text-sm">
              <Checkbox checked={selectedIds.includes(it.id)} onCheckedChange={() => onToggle(it.id)} />
              <span className="truncate">{renderLabel(it)}</span>
            </label>
          ))}
        </div>
      </ScrollArea>
    </details>
  );
}
